import { DEFAULT_QUERY_OPTIONS } from "#hooks/adapters/adapterUtils";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import { AddDefaultRecordAutomationDialog } from "#src/batteries-included-components/Dialogs/AddDefaultConfigurationDialog/AddDefaultRecordValueConfigurationDialog";
import { DeleteDefaultRecordValueConfigurationDialog } from "#src/batteries-included-components/Dialogs/DeleteDefaultRecordValueConfigurationDialog";
import { EditDefaultRecordValueConfigurationDialog } from "#src/batteries-included-components/Dialogs/EditDefaultRecordValueConfigurationDialog/EditDefaultRecordValueConfigurationDialog";
import { DRVC_QUERY_KEY } from "#src/components/hooks/adapters/useDefaultRecordValueConfig";
import { useGetManyUsers } from "#src/components/hooks/adapters/useUsers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  ItemAction,
  Link,
  NumberDataDisplay,
  StorageKeys,
} from "@validereinc/common-components";
import type {
  AssetTypeType,
  DefaultRecordValueConfigurationType,
} from "@validereinc/domain";
import {
  AssetType,
  DefaultRecordValueConfigurationDomain as DRVCDomain,
  DefaultRecordValueConfigurationTypeNames as DRVCTypeNames,
  DefaultRecordValueConfigurationFunctionNames,
  SortDirection,
} from "@validereinc/domain";
import React, { useMemo, useState } from "react";
import { DefaultRecordValueConfigurationDrawer } from "../../Drawers/DefaultRecordValueConfigurationDrawer/DefaultRecordValueConfigurationDrawer";

export const DefaultRecordValueConfigTablePanel = ({
  type,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: { type: AssetTypeType } & Pick<
  StorageKeys,
  "viewConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const [isAddRecordAutomationModalOpen, setIsAddRecordAutomationModalOpen] =
    useState(false);
  const [itemToDelete, setItemToDelete] = useState<
    DefaultRecordValueConfigurationType | undefined
  >();

  const [itemToEdit, setItemToEdit] = useState<
    DefaultRecordValueConfigurationType | undefined
  >();

  const { getTypeName, getUnitName, getPrecisionByType } =
    useMeasurementTypes();
  const [filters] = useStickyState<{ reporting_group_id?: string }>(
    {},
    viewConfigStorageKey
  );
  const { reporting_group_id } = filters;
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "measurement_type",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const { flowId, equipmentId, facilityId } = useParams<{
    flowId: string;
    equipmentId: string;
    facilityId: string;
  }>();

  const [sidebarState, setSidebarState] = useState<
    DefaultRecordValueConfigurationType | undefined
  >();

  let asset_id: string;
  switch (type) {
    case AssetType.FACILITY:
      asset_id = facilityId;
      break;
    case AssetType.EQUIPMENT:
      asset_id = equipmentId;
      break;
    case AssetType.FLOW:
    default:
      asset_id = flowId;
  }

  const isDRVCQueryEnabled = !!reporting_group_id;

  const DRVCFilters = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      asset_id,
      reporting_group_id,
    },
  };

  const DRVCQuery = useQuery({
    queryKey: [DRVC_QUERY_KEY, "getList", DRVCFilters],
    queryFn: () => DRVCDomain.getList(DRVCFilters),
    enabled: isDRVCQueryEnabled,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const rawItems = DRVCQuery?.data?.data ?? [];

  const items = rawItems.map((item) => item);

  const uniqueUserIds = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => (item as any)?.updated_by)
            .filter((item) => !!item)
        )
      ),
    [items]
  );

  const isLoadingUsersEnabled = uniqueUserIds.length > 0;
  const usersQueries = useGetManyUsers(uniqueUserIds);

  const userNamesByUUID = usersQueries.reduce<Record<string, string>>(
    (map, query) => {
      if (!query.data) {
        return map;
      }

      map[query.data.id] = query.data.name;
      return map;
    },
    {}
  );

  const isLoading =
    (isDRVCQueryEnabled && DRVCQuery.isLoading) ||
    (isLoadingUsersEnabled && usersQueries.some((q) => q.isLoading));

  const headers: Array<HeaderType<DefaultRecordValueConfigurationType>> = [
    {
      key: "measurement_type",
      label: "Property",
      isSortable: true,
      renderComponent: ({ item }) => getTypeName(item.measurement_type),
    },
    {
      key: "configuration.configuration_type",
      label: "Source Type",
      isSortable: true,
      renderComponent: ({ item }) => {
        const label = item.configuration?.configuration_type
          ? DRVCTypeNames[item.configuration?.configuration_type]
          : "-";
        return item.configuration ? (
          <Link
            onClick={() => {
              setSidebarState(item);
            }}
            label={label}
          />
        ) : (
          <>{label}</>
        );
      },
    },
    {
      key: "configuration.aggregate_function",
      label: "Function",
      renderComponent: ({ item }) =>
        item?.configuration?.aggregate_function
          ? DefaultRecordValueConfigurationFunctionNames[
              item?.configuration?.aggregate_function
            ]
          : "Default Value",
    },
    {
      key: "default_value",
      label: "Default Value",
      alignment: "right",
      tooltip:
        "Default Value will be used as the record source when automation is applied to a property that 1) does not have an automation configured 2) has an automation configured but a record value has not yet been auto-generated",
      isSortable: true,
      renderComponent: ({
        item,
      }: {
        item: DefaultRecordValueConfigurationType;
      }) => {
        const precision = getPrecisionByType(item.measurement_unit);

        return (
          <NumberDataDisplay
            value={item.default_value}
            unit={getUnitName(item.measurement_unit)}
            formattingOpts={
              precision
                ? {
                    fractionDigits: precision,
                  }
                : {
                    overrideOpts: {
                      maximumFractionDigits: 3,
                      minimumFractionDigits: 2,
                    },
                  }
            }
          />
        );
      },
    },
    {
      key: "description",
      label: "Description",
      renderComponent: ({ item }) => item.description ?? "-",
    },
    {
      key: "updated_by",
      label: "Updated By",
      renderComponent: ({ item }) =>
        userNamesByUUID?.[(item as any).updated_by] ?? "-",
    },
    {
      key: "updated_at",
      label: "Updated At",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={(item as any).updated_at}
          withTime
        />
      ),
    },
  ];

  const getItemActions = ({
    item,
  }: {
    item: DefaultRecordValueConfigurationType;
  }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setItemToEdit(item),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setItemToDelete(item),
        icon: "trash",
      },
    },
  ];

  const actionRow = [
    <Button
      key="add-record-automation"
      onClick={() => setIsAddRecordAutomationModalOpen(true)}
    >
      Create Record Automation
    </Button>,
  ];

  return (
    <>
      <EditDefaultRecordValueConfigurationDialog
        itemToEdit={itemToEdit}
        onClose={() => setItemToEdit(undefined)}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        dataTableProps={{
          headers,
          getItemActions,
          getItemId: (item) => item.id,
          items,
          isLoading,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: DRVCQuery.data?.total_entries,
          },
        }}
        panelProps={{ title: "Default Automations", actionRow }}
      />
      <DefaultRecordValueConfigurationDrawer
        isOpen={Boolean(sidebarState) ?? false}
        onClose={() => setSidebarState(undefined)}
        drvc={sidebarState}
        assetType={type}
      />
      <AddDefaultRecordAutomationDialog
        isOpen={isAddRecordAutomationModalOpen}
        onClose={() => setIsAddRecordAutomationModalOpen(false)}
        assetId={asset_id}
        reportingGroupId={reporting_group_id ?? ""}
      />
      {itemToDelete && (
        <DeleteDefaultRecordValueConfigurationDialog
          isOpen={!!itemToDelete}
          onClose={() => setItemToDelete(undefined)}
          itemToDelete={itemToDelete.id}
        />
      )}
    </>
  );
};
